import { TranslatePipe } from "@ngx-translate/core";
import { Component, Input, ChangeDetectionStrategy } from "@angular/core";
import { ComponentAdapterCounterHeating } from "src/components/shared-components/class/adapters/component-adapter-counter-heating";
import { GenericCounterGraphData } from "src/components/shared-components/class/generic-counter-graph-data";
import { WidgetCounterParent } from "src/components/shared-components/class/widget-counter-parent";
import { HeatingCounterMeasurementType } from "src/components/shared-components/enum/enumCounterMeasurementType";
import { ChartSerieKind } from "src/components/shared-components/enum/enumChart";
import { SkillNameList } from "src/components/shared-components/enum/enumSkillNameList";
import { SmartComponentSummaryData } from "src/components/shared-components/class/smart-components/smart-component-property-details";
import { WidgetCounterSkillExpandableComponent } from "../../widget-counter-skill-expandable/widget-counter-skill-expandable.component";
import { WidgetCounterBandComponent } from "../../widget-counter-band/widget-counter-band.component";
import { WidgetCounterBandTripleComponent } from "../../widget-counter-band-triple/widget-counter-band-triple.component";

@Component({
    selector: "app-counter-heat-meter",
    templateUrl: "./counter-heat-meter.component.html",
    styleUrls: ["./counter-heat-meter.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [WidgetCounterSkillExpandableComponent, WidgetCounterBandComponent, WidgetCounterBandTripleComponent, TranslatePipe]
})

/**
 * Heat Meter counter entry componet
 */
export class CounterHeatMeterComponent extends WidgetCounterParent<ComponentAdapterCounterHeating> {
  public chartSerieKind = ChartSerieKind;
  public chartBarEnergyHeat: GenericCounterGraphData =
    new GenericCounterGraphData();
  public chartBarVolume: GenericCounterGraphData =
    new GenericCounterGraphData();
  public chartLinePower: GenericCounterGraphData =
    new GenericCounterGraphData();
  public chartBarVolumeCSV: string = "";
  public chartBarEnergyHeatCSV: string = "";
  public chartLinePowerCSV: string = "";
  public showBandEnergyHeat: boolean = false;
  public showBandVolume: boolean = false;
  public showBandPower: boolean = false;
  public umEnergyHeat: string = "";
  public umVolume: string = "";
  public umPower: string = "";

  public previousMonthValue: string = "";
  public previousyearValue: string = "";
  public currentyearValue: string = "";
  public thermalEnergyPrevMonth: string = "";
  public thermalEnergyCurMonth: string = "";
  public thermalEnergyPrevYear: string = "";
  public thermalEnergyCurYear: string = "";
  public thermalPower: string = "";
  public volumePrevMonth: string = "";
  public volumeCurMonth: string = "";
  public volumePrevYear: string = "";
  public volumeCurYear: string = "";
  public errorChartEnergyHeat: boolean = false;
  public errorChartVolume: boolean = false;
  public errorChartPower: boolean = false;
  public skillType = SkillNameList;
  @Input() public headerSummary: SmartComponentSummaryData = null;

  protected async onComponentLoad(): Promise<void> {
    this.setMeasurements();

    try {
      await this._loaderService.presentLoaderWaiting();

      if (this.componentAdapter.EnergyHeat) {
        this.umEnergyHeat = this.componentAdapter.EnergyHeat.measureUnit;
        this.showBandEnergyHeat = true;
        try {
          if (this.componentAdapter.Summary) {
            this.chartBarEnergyHeat =
              await this.getSummaryYearlyForMonthConsumption(
                this.componentAdapter.IdEnvironment,
                this.componentAdapter.IdSmartComponentKind,
                SkillNameList.COUNTER_HEATING_ENERGY_HEAT
              );
            this.chartBarEnergyHeat = this.roundChartData(
              this.chartBarEnergyHeat,
              true
            );
            this.chartBarEnergyHeatCSV =
              await this.getSummaryYearlyForMonthConsumptionCSV(
                this.componentAdapter.IdEnvironment,
                this.componentAdapter.IdSmartComponentKind,
                SkillNameList.COUNTER_HEATING_ENERGY_HEAT
              );
          } else {
            this.chartBarEnergyHeat = await this.getYearlyForMonthConsumption(
              this.componentAdapter.EnergyHeat.address,
              this.componentAdapter.EnergyHeat.instanceGUID
            );
            this.chartBarEnergyHeat = this.roundChartData(
              this.chartBarEnergyHeat,
              true
            );
            if (this.chartBarEnergyHeat.data.length === 0) {
              this.showBandEnergyHeat = false;
            }
            this.chartBarEnergyHeatCSV =
              await this.getYearlyForMonthConsumptionCSV(
                this.componentAdapter.EnergyHeat.address,
                this.componentAdapter.EnergyHeat.instanceGUID
              );
          }
        } catch {
          this.errorChartEnergyHeat = true;
        }
      }

      if (this.componentAdapter.Volume) {
        this.umVolume = this.componentAdapter.Volume.measureUnit;
        this.showBandVolume = true;
        try {
          if (this.componentAdapter.Summary) {
            this.chartBarVolume =
              await this.getSummaryYearlyForMonthConsumption(
                this.componentAdapter.IdEnvironment,
                this.componentAdapter.IdSmartComponentKind,
                SkillNameList.COUNTER_HEATING_VOLUME
              );
            this.chartBarVolume = this.roundChartData(
              this.chartBarVolume,
              true
            );
            if (this.chartBarVolume.data.length === 0) {
              this.showBandVolume = false;
            }
            this.chartBarVolumeCSV =
              await this.getSummaryYearlyForMonthConsumptionCSV(
                this.componentAdapter.IdEnvironment,
                this.componentAdapter.IdSmartComponentKind,
                SkillNameList.COUNTER_HEATING_VOLUME
              );
          } else {
            this.chartBarVolume = await this.getYearlyForMonthConsumption(
              this.componentAdapter.Volume.address,
              this.componentAdapter.Volume.instanceGUID
            );
            this.chartBarVolume = this.roundChartData(
              this.chartBarVolume,
              true
            );
            this.chartBarVolumeCSV = await this.getYearlyForMonthConsumptionCSV(
              this.componentAdapter.Volume.address,
              this.componentAdapter.Volume.instanceGUID
            );
          }
        } catch {
          this.errorChartVolume = true;
        }
      }

      this.updateLineChart();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }
  }
  protected onComponentDestroy(): void {}

  /**
   * Update data on line chart when swipe
   * @param date
   */
  public async handleSwipeDataOnLineChart(date: Date) {
    this.setPeriodByDate(date);
    await this.updateLineChart();
  }

  /**
   * This method update the line chart data with the new date.
   */
  public async updateLineChart(): Promise<void> {
    if (this.componentAdapter.Power) {
      this.umPower = this.componentAdapter.Power.measureUnit;
      this.showBandPower = true;
      try {
        this.chartLinePower = await this.getPeaksData(
          this.componentAdapter.Power.address,
          this.componentAdapter.Power.instanceGUID
        );
        this.chartLinePower = this.roundChartData(this.chartLinePower, false);
        this.chartLinePowerCSV = await this.getPeaksDataCSV(
          this.componentAdapter.Power.address,
          this.componentAdapter.Power.instanceGUID
        );
      } catch {
        this.errorChartPower = true;
      }
    }
  }

  /**
   * This method set the displayed data defined throught the current "Measurements" associated to the
   * SmartComponent associated to this Widget.
   */
  private setMeasurements(): void {
    this.previousMonthValue = this.componentAdapter.PreviousMonth;
    this.previousyearValue =
      this._translateService.instant("dictionary.year") + " " + this.componentAdapter.PreviousYear;
    this.currentyearValue =
      this._translateService.instant("dictionary.year") + " " + this.componentAdapter.CurrentYear;

    this.thermalEnergyPrevMonth =
      this.componentAdapter.MeasurementMap.get(
        HeatingCounterMeasurementType[
          HeatingCounterMeasurementType.HEAT_THERMAL_ENERGY_PREVIOUS_MONTH
        ]
      ) || "---";
    this.thermalEnergyCurMonth =
      this.componentAdapter.MeasurementMap.get(
        HeatingCounterMeasurementType[
          HeatingCounterMeasurementType.HEAT_THERMAL_ENERGY_LAST_MONTH
        ]
      ) || "---";
    this.thermalEnergyPrevYear =
      this.componentAdapter.MeasurementMap.get(
        HeatingCounterMeasurementType[
          HeatingCounterMeasurementType.HEAT_THERMAL_ENERGY_PREVIOUS_YEAR
        ]
      ) || "---";
    this.thermalEnergyCurYear =
      this.componentAdapter.MeasurementMap.get(
        HeatingCounterMeasurementType[
          HeatingCounterMeasurementType.HEAT_THERMAL_ENERGY_LAST_YEAR
        ]
      ) || "---";
    this.thermalPower =
      this.componentAdapter.MeasurementMap.get(
        HeatingCounterMeasurementType[
          HeatingCounterMeasurementType.HEAT_ACTIVE_POWER
        ]
      ) || "---";
    this.volumePrevMonth =
      this.componentAdapter.MeasurementMap.get(
        HeatingCounterMeasurementType[
          HeatingCounterMeasurementType.HEAT_VOLUME_PREVIOUS_MONTH
        ]
      ) || "---";
    this.volumeCurMonth =
      this.componentAdapter.MeasurementMap.get(
        HeatingCounterMeasurementType[
          HeatingCounterMeasurementType.HEAT_VOLUME_LAST_MONTH
        ]
      ) || "---";
    this.volumePrevYear =
      this.componentAdapter.MeasurementMap.get(
        HeatingCounterMeasurementType[
          HeatingCounterMeasurementType.HEAT_VOLUME_PREVIOUS_YEAR
        ]
      ) || "---";
    this.volumeCurYear =
      this.componentAdapter.MeasurementMap.get(
        HeatingCounterMeasurementType[
          HeatingCounterMeasurementType.HEAT_VOLUME_LAST_YEAR
        ]
      ) || "---";
  }
}
